/**
 * Graphics calibration: how much tile texture this computer's GPU holds, and how fast
 * it takes new tiles, before the viewer would stutter — or crash.
 *
 * Why it exists: on some machines (reported on an Intel UHD 770, Windows) the
 * whole browser closes while panning and zooming a slide. Left to itself the
 * viewer kept up to 1000 decoded tiles per slide on the GPU (DMV's default
 * `tilesCacheSize`, about 1 GB for 512 px tiles) and uploaded every tile the
 * moment it was decoded, with no per-frame limit. Calibration measures both;
 * what it measures sizes the tile cache (tileBudget, applied in DmvEngine)
 * and the page's cache of compressed frames, half as much (frameBatches
 * sizePageFrames).
 *
 * It mimics what OpenLayers does for a slide, without a slide or a network:
 * WebGL 1 (what OL asks for), one RGB texture per 256px tile uploaded with
 * texImage2D (Direct3D pads it to RGBA, hence 4 bytes a pixel), one draw per
 * tile. Two stages:
 *
 *  1. Memory: hold ever more tiles on the GPU, 64 MB at a time up to 1 GB,
 *     drawing them all in turn so none can be paged out, until frames stall.
 *  2. Uploads: bursts of new tiles per frame (8 … 256), replacing old ones the
 *     way the tile cache churns, until frames stall.
 *
 * The browser closing IS a result. Before each step the step is written to
 * localStorage; a later visit that finds it still there knows the browser died
 * during that step, and records the last step that passed as the limit.
 */

import { HARD_SHRINK, MAX_SHRINK } from './engines/levelChoice';

export interface CalibrationResult {
  version: 1;
  /** ISO time calibration ran. */
  at: string;
  /** How it ended: every step ran, the GPU was reset, or the browser closed. */
  ending: 'complete' | 'gpu-lost' | 'browser-closed';
  /** Where it ended early, e.g. "Memory, 832 MB". */
  stoppedAt: string | null;
  /** The GPU as WebGL names it. */
  gpu: string | null;
  /** Largest tile set held with smooth frames, MB. */
  memoryMb: number;
  /** p95 frame time holding that set, ms. */
  frameP95Ms: number | null;
  /** Largest burst of new tiles per frame that stayed smooth. */
  burstPerFrame: number;
  /** New tiles uploaded per second at that burst. */
  uploadTilesPerSec: number;
  /** 0–100, see graphicsScore. */
  score: number;
}

export type CalibrationProgress = { stage: 'memory' | 'uploads'; label: string; fraction: number };

// ── Tunables ────────────────────────────────────────────────────────────────

export const TILE_PX = 256;
/** GPU bytes per tile: RGB is padded to RGBA on Direct3D. */
export const TILE_BYTES = TILE_PX * TILE_PX * 4;
export const MEMORY_STEP_MB = 64;
export const MEMORY_MAX_MB = 1024;
export const BURSTS = [8, 16, 32, 64, 128, 256] as const;
/** A frame slower than this at p95 is a stall: the step fails. */
export const SMOOTH_P95_MS = 50;
/** One frame this slow means the driver is near its reset timeout: the step
 *  fails however smooth the rest were. */
export const ABORT_FRAME_MS = 1000;
/** Tiles drawn per frame, in turn, so every held tile is touched regularly. */
const DRAWS_PER_FRAME = 256;
/** Tiles uploaded per frame while filling a memory step: gentle on purpose,
 *  so the memory stage measures holding, not uploading. */
const FILL_PER_FRAME = 32;
const HOLD_FRAMES = 45;
const BURST_FRAMES = 45;
/** A step that has not finished in this long has failed: stop it rather than
 *  crawl through its remaining frames at a second apiece. */
const STEP_MAX_MS = 5000;
/** Frames at the start of each step that are drawn but not judged: the ones
 *  right after a fill still carry its uploads. */
const WARMUP_FRAMES = 5;
/** Tiles kept resident during the upload stage: a working viewer's cache. */
const UPLOAD_BASELINE_MB = 128;
/** Score normalisers: a machine reaching both counts as 100. */
const SCORE_MEMORY_MB = 1024;
const SCORE_UPLOAD_MB_S = 1024;

const tilesPerMb = (1024 * 1024) / TILE_BYTES;

/**
 * Said wherever a WebGL context cannot be made. A browser switches WebGL off
 * for the rest of its run after the graphics driver resets — which is what a
 * slide too big for the graphics card causes — and only a full restart brings
 * it back; people saw "not available" and thought their computer lacked it.
 */
export const WEBGL_UNAVAILABLE =
  'WebGL is switched off in this browser, and the viewer needs it. ' +
  'That happens after the graphics reset: quit the browser completely, every window, and open it again.';

/** The attributes OpenLayers asks for (ol/webgl.js getContext), so the probe
 *  and the calibration test are treated by the driver as the viewer is. */
export const OL_CONTEXT_ATTRIBUTES: WebGLContextAttributes = { preserveDrawingBuffer: true, antialias: true };

/**
 * Whether this browser can make a WebGL context right now, the way the viewer
 * asks for one. The probe context is given back at once (contexts are few).
 */
export function webGlAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl', OL_CONTEXT_ATTRIBUTES);
    if (!gl) return false;
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return true;
  } catch {
    return false;
  }
}

// ── Scoring ─────────────────────────────────────────────────────────────────

/** 0–100: half memory held (out of 1 GB), half upload rate (out of 1 GB/s). */
export function graphicsScore(memoryMb: number, uploadTilesPerSec: number): number {
  const uploadMbS = (uploadTilesPerSec * TILE_BYTES) / (1024 * 1024);
  const m = Math.min(1, memoryMb / SCORE_MEMORY_MB);
  const u = Math.min(1, uploadMbS / SCORE_UPLOAD_MB_S);
  return Math.round(50 * m + 50 * u);
}

/** GPU memory for slide tiles on a computer that has not been calibrated:
 *  about what DMV's 1000-tile cache used for 256px tiles. */
export const DEFAULT_VIEWER_MEMORY_MB = 256;
/** Above this, more cache buys tiles nobody returns to soon, and those come
 *  back from the page's cache of compressed frames (frameBatches) in
 *  milliseconds anyway: decoded again, not downloaded. */
export const MAX_VIEWER_MEMORY_MB = 512;
/** Share of what calibration held smoothly that the viewer uses. Half, because
 *  a budget too big can close the browser and one too small costs a few ms of
 *  decoding: the browser, other tabs and real slides share what was measured. */
export const CALIBRATED_SHARE = 0.5;
/** Never less than this, even when calibration stalled below its first step:
 *  512 tiles of 256 px. It was 32 MB, set when calibration was designed and
 *  never measured on anything; the weakest computer measured (Intel UHD 770,
 *  2026-09-25) held 704 MB smoothly. 128 MB from 2026-10-12 (the user), which
 *  also puts the page's compressed tiles at 64 MB at least (frameBatches). */
export const MIN_VIEWER_MEMORY_MB = 128;

/**
 * GPU memory the viewer gives slide tiles on this computer, MB, for the whole
 * page: panes showing slides side by side share it (tileBudget).
 */
export function viewerMemoryMb(result: Pick<CalibrationResult, 'memoryMb'> | null): number {
  if (!result) return DEFAULT_VIEWER_MEMORY_MB;
  return Math.max(MIN_VIEWER_MEMORY_MB, Math.min(MAX_VIEWER_MEMORY_MB, Math.floor(result.memoryMb * CALIBRATED_SHARE)));
}

/** With each frame, the coarser level above it that the viewer preloads
 *  (one tile for every four): what a zoom draws in the meantime. */
export const PRELOAD_SHARE = 1 / 4;

/** A pane and the tiles of the slide it shows. The pane is sized in the pixels
 *  levels are chosen by: its CSS pixels times the level scale (scaledPane),
 *  the screen's density where the budget affords it (affordableLevelScale). */
export interface PaneTiles {
  tileWidth: number;
  tileHeight: number;
  paneWidth: number;
  paneHeight: number;
}

/** The pane as OpenLayers sees it when levels are chosen `scale` device
 *  pixels per CSS pixel (levelChoice.scaleLevelChoice): `scale` times wider
 *  and taller in tiles. */
export function scaledPane<T extends PaneTiles>(pane: T, scale: number): T {
  return { ...pane, paneWidth: pane.paneWidth * scale, paneHeight: pane.paneHeight * scale };
}

/** The tiles that cover the pane when the level drawn is shrunk `shrink`
 *  times, counting the partial tile on each side. */
export function frameTiles(pane: PaneTiles, shrink: number): number {
  const across = Math.ceil((pane.paneWidth * shrink) / Math.max(1, pane.tileWidth)) + 1;
  const down = Math.ceil((pane.paneHeight * shrink) / Math.max(1, pane.tileHeight)) + 1;
  return across * down;
}

/** This pane's share of the page's tile memory, in tiles of the slide's own
 *  size: 256 MB is 1024 tiles of 256 px, or 256 of 512 px. GPU bytes per
 *  tile are RGBA (Direct3D pads RGB), the same count as TILE_BYTES for 256 px. */
export function budgetTiles(opts: Pick<PaneTiles, 'tileWidth' | 'tileHeight'> & { memoryMb: number; panes: number }): number {
  const tileBytes = Math.max(1, opts.tileWidth) * Math.max(1, opts.tileHeight) * 4;
  const share = (opts.memoryMb * 1024 * 1024) / Math.max(1, opts.panes);
  return Math.floor(share / tileBytes);
}

/**
 * How many device pixels per CSS pixel levels are chosen by (levelChoice):
 * `wanted`, the screen's density, as long as a frame of a level shrunk
 * MAX_SHRINK with its preloaded parents still fits the pane's share of the
 * budget (`budget`, budgetTiles); otherwise the largest scale below it, in
 * quarter steps, that does, down to 1: OpenLayers' own choice, per CSS pixel,
 * which tileBudget's floor always affords. The pane here is in CSS pixels.
 */
export function affordableLevelScale(opts: PaneTiles & { budget: number; wanted: number }): number {
  for (let scale = opts.wanted; scale > 1; scale -= 0.25) {
    if (frameTiles(scaledPane(opts, scale), MAX_SHRINK) * (1 + PRELOAD_SHARE) <= opts.budget) return scale;
  }
  return 1;
}

/**
 * How many tiles one pane may keep decoded: its share of the page's budget at
 * this slide's tile size, never less than one frame at the deepest shrink the
 * level choice makes without asking (MAX_SHRINK, levelChoice) with its
 * preloaded parents. OpenLayers' renderer expires its cache after every frame
 * without sparing the tiles it has just drawn, so a frame larger than the
 * cache is fetched again every frame and never settles; the floor is what
 * rules that out whatever the budget.
 *
 * A slide's tiles are 256 or 512 px on a side, so the budget is in tiles of
 * ITS size (budgetTiles). The pane is in the pixels levels are chosen by
 * (scaledPane), so the floor grows with the level scale.
 */
export function tileBudget(opts: PaneTiles & { memoryMb: number; panes: number }): number {
  const floor = Math.ceil(frameTiles(opts, MAX_SHRINK) * (1 + PRELOAD_SHARE));
  return Math.max(floor, budgetTiles(opts));
}

/**
 * How far the finer of two levels may be shrunk where neither is clean
 * (levelChoice): as far as a frame of it, with its preloaded parents, still
 * fits the pane's tile budget, between MAX_SHRINK (which tileBudget's floor
 * always affords) and HARD_SHRINK.
 */
export function affordableShrink(opts: PaneTiles & { budget: number }): number {
  // A frame grows with the square of the shrink; quarter steps are plenty.
  for (let shrink = HARD_SHRINK; shrink > MAX_SHRINK; shrink -= 0.25) {
    if (frameTiles(opts, shrink) * (1 + PRELOAD_SHARE) <= opts.budget) return shrink;
  }
  return MAX_SHRINK;
}

// Not done, on purpose: OpenLayers asks for 4× multisampling on the slide
// canvas, which stores the drawing buffer four times over — about 118 MB on a
// 2560×1440 screen under Direct3D, on top of the ~44 MB the buffer, its depth
// and the post-process texture cost anyway. Refusing it (antialias:false when
// the context is made) changes nothing inside the slide, since tiles are
// textured squares sharing edges; only the slide's outer edge would step when
// rotated. Left out 2026-09-25: it only matters on very-low-memory graphics,
// which the viewer does not target.

export function p95(values: number[]): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(s.length * 0.95))];
}

// ── Persistence ─────────────────────────────────────────────────────────────

const RESULT_KEY = 'tr.graphicsCalibration';
const RUNNING_KEY = 'tr.graphicsCalibration.running';

type Partial = Omit<CalibrationResult, 'ending' | 'stoppedAt' | 'score'>;
interface Running {
  step: string;
  partial: Partial;
  /** Date.now() of the last beat; a live run refreshes it every second. */
  at?: number;
}

/** A marker younger than this belongs to a run still going, in another tab. */
const RUNNING_STALE_MS = 5000;
const MARK_EVERY_MS = 1000;

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function finish(partial: Partial, ending: CalibrationResult['ending'], stoppedAt: string | null): CalibrationResult {
  return {
    ...partial,
    ending,
    stoppedAt,
    score: graphicsScore(partial.memoryMb, partial.uploadTilesPerSec),
  };
}

export function loadResult(store: Storage | null = storage()): CalibrationResult | null {
  try {
    const raw = store?.getItem(RESULT_KEY);
    const r = raw ? (JSON.parse(raw) as CalibrationResult) : null;
    return r?.version === 1 ? r : null;
  } catch {
    return null;
  }
}

function save(result: CalibrationResult, store: Storage | null): void {
  try {
    store?.setItem(RESULT_KEY, JSON.stringify(result));
    store?.removeItem(RUNNING_KEY);
  } catch {
    /* private browsing: the result shows once and is not kept */
  }
}

function mark(running: Running, store: Storage | null): void {
  try {
    store?.setItem(RUNNING_KEY, JSON.stringify({ ...running, at: Date.now() }));
  } catch {
    /* without storage a crash cannot be recorded; the test still runs */
  }
}

/**
 * A test the browser did not survive: its last step is the limit. Call on
 * page load; returns the recorded result, or null when nothing was cut short.
 */
export function recoverInterruptedRun(
  store: Storage | null = storage(),
  now: number = Date.now(),
): CalibrationResult | null {
  try {
    const raw = store?.getItem(RUNNING_KEY);
    if (!raw) return null;
    const { step, partial, at } = JSON.parse(raw) as Running;
    // Still beating: calibration is running in another tab of this browser,
    // not a browser that died. Its marker is left for it.
    if (at !== undefined && now - at < RUNNING_STALE_MS) return null;
    const result = finish(partial, 'browser-closed', step);
    save(result, store);
    return result;
  } catch {
    store?.removeItem(RUNNING_KEY);
    return null;
  }
}

// ── The test ────────────────────────────────────────────────────────────────

const VERTEX = `
attribute vec2 a_corner;
uniform vec4 u_rect; // x, y, w, h in clip space
varying vec2 v_uv;
void main() {
  v_uv = a_corner;
  gl_Position = vec4(u_rect.xy + a_corner * u_rect.zw, 0.0, 1.0);
}`;
const FRAGMENT = `
precision mediump float;
uniform sampler2D u_tile;
varying vec2 v_uv;
void main() { gl_FragColor = texture2D(u_tile, v_uv); }`;

class TestGl {
  readonly gl: WebGLRenderingContext;
  private rect: WebGLUniformLocation;
  private tiles: Uint8Array[];
  private uploads = 0;
  lost = false;

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl', OL_CONTEXT_ATTRIBUTES);
    if (!gl) throw new Error(WEBGL_UNAVAILABLE);
    this.gl = gl;
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.lost = true;
    });
    const program = gl.createProgram()!;
    for (const [type, src] of [
      [gl.VERTEX_SHADER, VERTEX],
      [gl.FRAGMENT_SHADER, FRAGMENT],
    ] as const) {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      gl.attachShader(program, s);
    }
    gl.linkProgram(program);
    gl.useProgram(program);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);
    const corner = gl.getAttribLocation(program, 'a_corner');
    gl.enableVertexAttribArray(corner);
    gl.vertexAttribPointer(corner, 2, gl.FLOAT, false, 0, 0);
    this.rect = gl.getUniformLocation(program, 'u_rect')!;
    // A few distinct noisy tiles, like real tissue rather than one flat colour.
    this.tiles = Array.from({ length: 4 }, (_, k) => {
      const t = new Uint8Array(TILE_PX * TILE_PX * 3);
      let x = 2463534242 + k;
      for (let i = 0; i < t.length; i++) {
        x ^= x << 13;
        x ^= x >>> 17;
        x ^= x << 5;
        t[i] = x & 0xff;
      }
      return t;
    });
  }

  gpuName(): string | null {
    const gl = this.gl;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    return (ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER)) ?? null;
  }

  /** A new tile texture, uploaded the way OpenLayers uploads a data tile. */
  upload(): WebGLTexture {
    const gl = this.gl;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    // OL reads the unpack alignment back before every upload; so do we.
    const old = gl.getParameter(gl.UNPACK_ALIGNMENT);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 8);
    const data = this.tiles[this.uploads++ % this.tiles.length];
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, TILE_PX, TILE_PX, 0, gl.RGB, gl.UNSIGNED_BYTE, data);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, old);
    return tex;
  }

  /** Draw a window of the held tiles as a grid over the whole canvas. */
  draw(textures: WebGLTexture[], start: number): void {
    const gl = this.gl;
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.clearColor(0.1, 0.1, 0.1, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    const n = Math.min(DRAWS_PER_FRAME, textures.length);
    const cols = 16;
    const rows = Math.ceil(DRAWS_PER_FRAME / cols);
    const w = 2 / cols;
    const h = 2 / rows;
    for (let i = 0; i < n; i++) {
      gl.bindTexture(gl.TEXTURE_2D, textures[(start + i) % textures.length]);
      gl.uniform4f(this.rect, -1 + (i % cols) * w, 1 - (Math.floor(i / cols) + 1) * h, w, h);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
  }

  outOfMemory(): boolean {
    return this.gl.getError() === this.gl.OUT_OF_MEMORY;
  }

  release(textures: WebGLTexture[]): void {
    for (const t of textures) this.gl.deleteTexture(t);
  }

  dispose(): void {
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}

const nextFrame = () => new Promise<number>((r) => requestAnimationFrame(r));

class GpuLost extends Error {}


/**
 * Run the test on `canvas` (sized to the screen by the caller). Resolves with
 * the result, already saved; rejects on abort. A browser that closes mid-run
 * is picked up by recoverInterruptedRun on the next visit.
 */
export async function runGraphicsCalibration(
  canvas: HTMLCanvasElement,
  onProgress: (p: CalibrationProgress) => void,
  signal: AbortSignal,
): Promise<CalibrationResult> {
  const store = storage();
  const g = new TestGl(canvas);
  const partial: Partial = {
    version: 1,
    at: new Date().toISOString(),
    gpu: g.gpuName(),
    memoryMb: 0,
    frameP95Ms: null,
    burstPerFrame: 0,
    uploadTilesPerSec: 0,
  };
  const held: WebGLTexture[] = [];
  let cursor = 0;
  let last = await nextFrame();
  let step = '';
  let marked = 0;

  /** One frame: optional work, a draw, and the time since the last frame. */
  const frame = async (work?: () => void): Promise<number> => {
    if (signal.aborted) throw new DOMException('Stopped', 'AbortError');
    work?.();
    g.draw(held, cursor);
    cursor += DRAWS_PER_FRAME;
    const now = await nextFrame();
    const dt = now - last;
    last = now;
    if (g.lost) throw new GpuLost();
    // The marker's heartbeat, so another tab can tell this run from a crash.
    if (step && Date.now() - marked > MARK_EVERY_MS) {
      mark({ step, partial }, store);
      marked = Date.now();
    }
    return dt;
  };

  /**
   * `count` frames of a step after a short warm-up, or null as soon as it has
   * failed: more slow frames than a p95 allows, one near the driver's timeout,
   * or too long in all.
   */
  const attempt = async (count: number, work?: () => void): Promise<number[] | null> => {
    const times: number[] = [];
    const allowed = Math.floor(count * 0.05);
    let slow = 0;
    let total = 0;
    for (let f = 0; f < WARMUP_FRAMES + count; f++) {
      const dt = await frame(work);
      total += dt;
      if (dt > ABORT_FRAME_MS || total > STEP_MAX_MS) return null;
      if (f < WARMUP_FRAMES) continue;
      times.push(dt);
      if (dt > SMOOTH_P95_MS && ++slow > allowed) return null;
    }
    return times;
  };
  /** A step fails only if it fails twice: one hiccup (a garbage collection,
   *  another tab) is not this computer's limit. */
  const measure = async (count: number, work?: () => void): Promise<number[] | null> =>
    (await attempt(count, work)) ?? (await attempt(count, work));

  const end = (ending: CalibrationResult['ending'], stoppedAt: string | null) => {
    const result = finish(partial, ending, stoppedAt);
    save(result, store);
    return result;
  };

  try {
    // 1. Memory.
    const steps = MEMORY_MAX_MB / MEMORY_STEP_MB;
    let memoryStopped = false;
    for (let i = 1; i <= steps && !memoryStopped; i++) {
      const mb = i * MEMORY_STEP_MB;
      step = `Memory, ${mb} MB`;
      mark({ step, partial }, store);
      onProgress({ stage: 'memory', label: step, fraction: (i - 1) / steps });
      const target = mb * tilesPerMb;
      // Filling uploads 8 MB a frame, so slow frames are expected; only a
      // frame near the driver's timeout, or taking all day, fails it.
      let filled = true;
      for (let spent = 0; filled && held.length < target; ) {
        const dt = await frame(() => {
          for (let k = 0; k < FILL_PER_FRAME && held.length < target; k++) held.push(g.upload());
        });
        spent += dt;
        filled = dt <= ABORT_FRAME_MS && spent <= STEP_MAX_MS;
      }
      const times = filled ? await measure(HOLD_FRAMES) : null;
      if (!times || g.outOfMemory()) {
        memoryStopped = true;
      } else {
        partial.memoryMb = mb;
        partial.frameP95Ms = Math.round(p95(times) * 10) / 10;
      }
    }
    const memoryStop = memoryStopped ? step : null;

    // 2. Uploads, over a working-sized cache that churns as the viewer's does.
    g.release(held.splice(Math.min(held.length, UPLOAD_BASELINE_MB * tilesPerMb)));
    const baseline = held.length;
    let uploadStop: string | null = null;
    for (let i = 0; i < BURSTS.length; i++) {
      const burst = BURSTS[i];
      step = `Uploads, ${burst} tiles a frame`;
      mark({ step, partial }, store);
      onProgress({ stage: 'uploads', label: step, fraction: i / BURSTS.length });
      let uploaded = 0;
      const times = await measure(BURST_FRAMES, () => {
        for (let k = 0; k < burst; k++) held.push(g.upload());
        uploaded += burst;
        if (held.length > baseline) g.release(held.splice(0, held.length - baseline));
      });
      if (!times || g.outOfMemory()) {
        uploadStop = step;
        break;
      }
      const elapsedS = times.reduce((a, b) => a + b, 0) / 1000;
      partial.burstPerFrame = burst;
      partial.uploadTilesPerSec = Math.max(partial.uploadTilesPerSec, Math.round(uploaded / elapsedS));
    }
    onProgress({ stage: 'uploads', label: 'Done', fraction: 1 });
    return end('complete', memoryStop ?? uploadStop);
  } catch (err) {
    // A lost context is a GPU reset: the limit, recorded like a closed browser.
    if (err instanceof GpuLost) return end('gpu-lost', step);
    // Stopped by the person: nothing measured is kept.
    try {
      store?.removeItem(RUNNING_KEY);
    } catch {
      /* nothing to clear */
    }
    throw err;
  } finally {
    try {
      g.release(held);
      g.dispose();
    } catch {
      /* the context may already be gone */
    }
  }
}

/** Plain-text summary, for pasting into a support message. */
export function describeResult(r: CalibrationResult): string {
  return [
    `TileRiver graphics calibration, ${r.at}`,
    `GPU: ${r.gpu ?? 'unknown'}`,
    `Graphics score: ${r.score}/100`,
    `Tile memory held smoothly: ${r.memoryMb} MB${r.frameP95Ms != null ? ` (p95 frame ${r.frameP95Ms} ms)` : ''}`,
    `Upload rate: ${r.uploadTilesPerSec} tiles/s, bursts up to ${r.burstPerFrame} tiles a frame`,
    `Ended: ${r.ending}${r.stoppedAt ? ` at ${r.stoppedAt}` : ''}`,
    '',
    'Viewer configuration on this computer:',
    `Tile memory: ${viewerMemoryMb(r)} MB (${Math.round(CALIBRATED_SHARE * 100)}% of ${r.memoryMb} MB, at least ${MIN_VIEWER_MEMORY_MB} MB, at most ${MAX_VIEWER_MEMORY_MB} MB; ${DEFAULT_VIEWER_MEMORY_MB} MB uncalibrated)`,
    `  about ${Math.floor((viewerMemoryMb(r) * 1024 * 1024) / TILE_BYTES).toLocaleString('en-US')} tiles of ${TILE_PX} px, shared by the panes on screen, never less than a frame of a level shrunk ${MAX_SHRINK}x a pane`,
    '',
    `Screen: ${window.screen.width}x${window.screen.height} @${window.devicePixelRatio}x`,
    `Browser: ${navigator.userAgent}`,
  ].join('\n');
}
